import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";
import { faqs, howItWorks, AnalyticsHeader } from "./helpers";

import SeatMap3dPreview from "public/images/magic/magic_3d_venue.png";
import BannerImage from "public/images/magic/10-Game-Plans-Banner.jpg";
import BackgroundImage from "public/images/magic/magic_ChooseYourGamesBanner.jpeg";

import type { TeamConfig } from "../types";

import LogoImage from "public/images/magic/global-logo.png";

const config: TeamConfig = {
  teamSchema: "orlandomagic_10pack",
  browserTitle: "Magic 10-Game Plans",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Orlando Magic",

  dynamicPricing: {
    enabled: true,
    dontOpenModal: true,
    list: [
      {
        id: 1,
        discount: 0,
        minSelectedAmount: 1,
        updatedAccountGroup: 0,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Congrats! You've selected one 10-Game Plan. Add another or checkout now.",
      },
      {
        id: 2,
        discount: 0,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Congrats! You've selected two 10-Game Plans. Add another or checkout now.",
      },
      {
        id: 3,
        discount: 0,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Congrats! You've selected three 10-Game Plans. Add another or checkout now.",
      },
      {
        id: 4,
        discount: 0,
        minSelectedAmount: 4,
        updatedAccountGroup: 0,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Congrats - you've selected four 10-Game Plans! Checkout now.",
      },
    ],
  },

  manageCard: true,

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00070-magic-sec",
    mapId: "main",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
    hideViewTicketsInThankYouPage: true,
    hideAllUpcomingGames: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: 3,
    allowedPaymentFailCount: 3,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-XTHMZHVGB4",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
    AnalyticsHeader,
    trackGoogleAnalyticsOnCheckout: true,
    customAnalyticsConversionEvent: "purchase", // default is 'conversion'
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/magic/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage.src + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage.src,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: true,
      showLogoWithTitle: false,
      hideDateAndTime: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    checkoutStatusPage: {
      backgroundColor: "unset",
    },

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,

      hideGameFiltersOnFindGames: true,
    },

    findGames: {
      eventDateTimeReplacement: [
        { eventCode: "MQ252610", label: "Starting at $1,000!" }, // Big Game Plan
        { eventCode: "WK252610", label: "Starting at $580!" }, // Weekend Plan
        { eventCode: "ES252610", label: "Starting at $540!" }, // Eastern Conference Plan
        { eventCode: "WS252610", label: "Starting at $610!" }, // Western Conference Plan
      ],
    },

    checkoutPage: {
      checkoutDisclaimer:
        "Your Ticket Plan will automatically renew. Next season, you don't have to do a thing for your ticket plan renewal, unless of course, you choose to discontinue your tickets, which will be an option. Each year, you'll be notified of any price or benefit changes and the deadline by which you can elect to opt-out if you choose. An Orlando Magic sales representative will be in touch following your purchase to answer any additional questions and collect your signature on the ticket plan agreement.",
    },
  },

  colors: {
    primary: "#0050B5",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      findGamesTitle: "10-Game Plans",
      progressBarText: "Add 1 Plan to continue!",
      bottomBarEventsDescription: "Game Plan",
      purchaseSeatTabTitle: "Choose Plan",
      checkoutZipCodeFailText: (
        <>
          We&apos;re sorry, but the zip code on your billing address is
          currently outside the eligible areas for this offer. For further
          questions, please contact the Orlando Magic at{" "}
          <Link
            href="mailto:help@orlandomagic.com"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
          >
            help@orlandomagic.com
          </Link>
          .
        </>
      ),
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your purchase with the Orlando Magic! You will be notified when your tickets are available.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
